'use client';

import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Card, CardContent } from '@/shared/components/ui/card';
import { BookOpen, Mic, Video } from 'lucide-react';

// Story-only: the three formats one story becomes (audio · book · cartoon).
const features = [
  {
    icon: Mic,
    titleKey: 'features.multimedia.title',
    descriptionKey: 'features.multimedia.description',
    gradient: 'from-brand-purple to-brand-pink',
    bgLight: 'bg-brand-purple-light',
    highlights: ['features.multimedia.h1', 'features.multimedia.h2', 'features.multimedia.h3'],
    href: '/voices',
  },
  {
    icon: BookOpen,
    titleKey: 'features.personalized.title',
    descriptionKey: 'features.personalized.description',
    gradient: 'from-brand-blue to-brand-purple',
    bgLight: 'bg-brand-blue-light',
    highlights: ['features.personalized.h1', 'features.personalized.h2', 'features.personalized.h3'],
    href: '/stories',
  },
  {
    icon: Video,
    titleKey: 'features.video.title',
    descriptionKey: 'features.video.description',
    gradient: 'from-brand-pink to-brand-orange',
    bgLight: 'bg-brand-pink-light',
    highlights: ['features.video.h1', 'features.video.h2', 'features.video.h3'],
    href: '/gallery',
  },
];

export default function FeaturesSection() {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <section data-testid="landing-features-section" className="py-14 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('features.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('features.description')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link
                key={feature.titleKey}
                href={`/${locale}${feature.href}`}
                className="block"
              >
                <Card
                  className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden bg-card h-full"
                >
                  <div className={`h-1 bg-gradient-to-r ${feature.gradient}`} />
                  {/* Compact on a phone: the three formats are a LIST of what you
                      get, not three essays — the chips repeat the sentence above
                      them and only earn their space on a wide screen. */}
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-3 sm:gap-5">
                      <div className={`size-11 sm:size-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-lg shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="size-5 sm:size-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-xl font-bold text-foreground mb-1 sm:mb-2">
                          {t(feature.titleKey)}
                        </h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {t(feature.descriptionKey)}
                        </p>
                        <div className="mt-3 hidden flex-wrap gap-2 lg:flex">
                          {feature.highlights.map((key) => (
                            <span
                              key={key}
                              className={`text-xs font-medium px-3 py-1 rounded-full ${feature.bgLight} text-foreground/80`}
                            >
                              {t(key)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
